import React, { useState, ChangeEvent, useRef } from 'react';
import { Button, Modal, Table } from 'react-bootstrap-v5';
import { useIntl } from 'react-intl';
import * as XLSX from 'xlsx';
import { PAYMENT_IMPORT_HEADERS } from '../../constants/common';

interface ExcelRow {
    [key: string]: any;
}

interface ExcelImportModalProps {
    show: boolean;
    handleClose: () => void;
    onHide?: () => void;
    handleImport: (data: ExcelRow[]) => void;
    title: string
    submitText: string
}

const ExcelToObjectImportModal: React.FC<ExcelImportModalProps> = ({
    show,
    handleClose,
    onHide,
    handleImport,
    title,
    submitText
}) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const intl = useIntl();
    const [rows, setRows] = useState<ExcelRow[]>([]);
    const [file, setFile] = useState<File | null>(null)
    
    // derive header list by unioning all keys present in any row; maintain order
    // then sort according to PAYMENT_IMPORT_HEADERS constant
    const allHeaders = React.useMemo(() => {
        const seen: string[] = [];
        rows.forEach(r => {
            Object.keys(r).forEach(k => {
                if (!seen.includes(k)) seen.push(k);
            });
        });
        const order = PAYMENT_IMPORT_HEADERS.map(h => h.toLowerCase());
        seen.sort((a, b) => {
            const ia = order.indexOf(a.toLowerCase());
            const ib = order.indexOf(b.toLowerCase());
            if (ia === -1 && ib === -1) return a.localeCompare(b);
            if (ia === -1) return 1;
            if (ib === -1) return -1;
            return ia - ib;
        });
        return seen;
    }, [rows]);

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files[0]) {
            const file = files[0];
            const reader = new FileReader();
            reader.onload = (evt) => {
                const bstr = evt.target?.result;
                const wb = XLSX.read(bstr, { 
                    type: 'binary'
                });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                let data: any = XLSX.utils.sheet_to_json(ws);
                // some cells (especially dates) come through as Excel serial numbers
                // convert any numeric value that looks like a date into a d/m/Y string
                data = data.map((row: any) => {
                    const newRow: any = {};
                    Object.entries(row).forEach(([k, v]) => {
                        // only format the transaction date column from serial numbers
                        if (
                            k.toLowerCase() === 'ngày giao dịch' &&
                            typeof v === 'number'
                        ) {
                            const parsed = XLSX.SSF.parse_date_code(v);
                            if (parsed && parsed.y) {
                                // build dd/mm/yyyy
                                const d = parsed.d.toString().padStart(2, '0');
                                const m = parsed.m.toString().padStart(2, '0');
                                newRow[k] = `${d}/${m}/${parsed.y}`;
                                return;
                            }
                        }
                        newRow[k] = v;
                    });
                    return newRow;
                });
                setRows(data);
            };
            reader.readAsBinaryString(file);
            setFile(file)
        }
    };

    const handleSubmit = () => {
        if (handleImport) {
            handleImport(rows);
        }
        setFile(null);
        handleClose();
    }

    const openInputFile = () => {
        fileInputRef.current?.click();
    };

    return (
        <Modal
            show={show ?? false}
            onHide={() => onHide?.()}
            size='xl'
            dialogClassName="modal-content-custom"
        >
            <Modal.Header>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="d-flex flex-column align-items-center my-4">
                    <i className="btn fs-1 btn-icon btn-circle bi bi bi-cloud-arrow-up-fill bg-light-primary text-primary"></i>
                    <h3 className="mt-3">Chọn tệp để tải lên</h3>
                    <p className="text-muted">
                        {'XLS, XLSX'}
                    </p>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm text-white"
                        onClick={openInputFile}
                    >
                        <i className="bi bi bi-cloud-arrow-up-fill fs-4 text-white"></i> Chọn tệp
                    </button>
                    {file?.name && (<p className="">{file.name}</p>)}
                </div>
                <input ref={fileInputRef} style={{ display: "none" }} type="file" accept=".xlsx,.xls" onChange={handleFileChange} />
                <div className='table-responsive-y'>
                    <Table responsive striped bordered hover size="sm">
                        <thead>
                            {allHeaders.length > 0 && (
                                <tr>
                                    {allHeaders.map((header, index) => (
                                        <th key={index}>{header}</th>
                                    ))}
                                </tr>
                            )}
                        </thead>
                        <tbody>
                            {rows.map((row, index) => (
                                <tr key={index}>
                                    {allHeaders.map((h, idx) => (
                                        <td key={idx}>{row[h] ?? ''}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success" onClick={() => handleSubmit()}>
                    {submitText}
                </Button>
                <Button variant="secondary" onClick={handleClose}>
                    {intl.formatMessage({ id: 'COMMON.CLOSE' })}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ExcelToObjectImportModal;
